import { PageableDataSourceProxy } from "./table-paging-data-source-proxy";
import { PageableDataSource } from "./table-paging";
import { TableController } from "./table";
import { LocalizationService } from "../../localization/localization";
//import * as agGrid from "ag-grid";
declare let agGrid: any;

export interface TableFunctionality {
  isEnabled: boolean;
  extendGridOptions?: (options: SdkGridOptions) => void;
}

export interface SdkGridOptions {
  _sd_whenGridReady: (callback: () => void) => void;
  api: any;
}

const defaultPageSize = 10;
export class PaginationFunctionality implements TableFunctionality {
  private dataSourceProxy: PageableDataSourceProxy;
  private lastTrackedDataSource: PageableDataSource;
  private infiniteRowModel: any;

  constructor(
    private table: TableController,
    private $scope: ng.IScope,
    private localizationService: LocalizationService
  ) {}

  public get isBusy() {
    return this.dataSourceProxy.isBusy;
  }

  public onBodyScroll(params: any, params2: any) {
    this.loadMoreIfNeeded();
  }

  public extendGridOptions(gridOptions: SdkGridOptions) {
    // Initialize our current datasource, and if a new one is bound then react.
    this.onNewDataSource(this.table.pageableDataSource, (proxy) => ((<any>gridOptions).datasource = proxy));
    this.$scope.$watch(
      () => {
        return (<TableController>this.table).pageableDataSource;
      },
      (newValue: PageableDataSource) => {
        if (this.lastTrackedDataSource !== newValue) {
          this.onNewDataSource(newValue, (proxy) => this.table.gridOptions.api.setDatasource(proxy));
        }
      }
    );

    let untypedAgGridOptions = <any>gridOptions;
    untypedAgGridOptions.cacheBlockSize = this.lastTrackedDataSource.pageSize || defaultPageSize;
    untypedAgGridOptions.enableServerSideSorting = this.lastTrackedDataSource.allowServerSorting;
    untypedAgGridOptions.enableServerSideFilter = this.lastTrackedDataSource.allowServerFiltering;
    untypedAgGridOptions.suppressScrollOnNewData = true;
    let loadingText = this.localizationService.getLocalizedString(
      "ShellCommon",
      "_CONTROLS_TABLE_PAGING_LOADING_TEXT_"
    );
    untypedAgGridOptions.overlayLoadingTemplate = `<span class="ag-overlay-loading-center">${loadingText}</span>`;

    gridOptions._sd_whenGridReady(() => this.onGridReady(gridOptions));
  }

  public getCurrentDataLength() {
    return this.table.gridOptions.api.rowModel.infiniteCache.virtualRowCount;
  }

  public get isEnabled() {
    return !!(<any>this.table).pageableDataSource;
  }

  public get dataSource() {
    return this.dataSourceProxy;
  }

  public onSortChanged() {
    if (this.infiniteRowModel) {
      this.infiniteRowModel.reset();
    }

    this.table.gridOptions.api.rowModel.setRowData([]);
  }

  public setFilterValues(columnName: string, filteredValues: string[]) {
    let filterModel = this.table.gridOptions.api.getFilterModel();
    filterModel[columnName] = {
      filter: filteredValues
    };

    if (!filterModel[columnName].filter || filterModel[columnName].filter.length == 0) {
      delete filterModel[columnName];
    }

    this.table.gridOptions.api.rowModel.setRowData([]);
    this.table.gridOptions.api.setFilterModel(filterModel);
  }

  public getRowData(rowData: any) {
    for (let i = 0; i < this.infiniteRowModel.infiniteCache.virtualRowCount; i++) {
      let matchingData = this.infiniteRowModel.infiniteCache.getRow(i).data;
      if (matchingData == rowData) {
        return matchingData;
      }
    }
  }

  private loadMoreIfNeeded() {
    let haveWeRenderedToTheEndOfOurCurrentRowCount =
      this.table.gridOptions.api.rowRenderer.lastRenderedRow + 1 >= this.table.gridOptions.api.rowModel.getRowCount();
    let haveWeNotYetFoundTheMaxRow = this.infiniteRowModel && !this.infiniteRowModel.infiniteCache.maxRowFound;

    if (haveWeRenderedToTheEndOfOurCurrentRowCount && haveWeNotYetFoundTheMaxRow) {
      this.infiniteRowModel.getRow(this.infiniteRowModel.infiniteCache.getVirtualRowCount() - 1);
    }
  }

  private onNewDataSource(dataSource: PageableDataSource, setter: (dataSourceProxy: PageableDataSourceProxy) => void) {
    this.lastTrackedDataSource = this.table.pageableDataSource;
    this.dataSourceProxy = new PageableDataSourceProxy(this.lastTrackedDataSource);
    setter(this.dataSourceProxy);

    if (this.table.gridOptions.api) {
      this.table.gridOptions.api.rowModel.setRowData([]);
    }

    if (this.infiniteRowModel) {
      this.infiniteRowModel.reset();
    }
  }

  // When AG Grid's infinite model cache is updated, we push these changes out to the inmemory rowmodel.
  private onInfiniteModelCacheUpdated(infiniteModel: any, gridOptions: any) {
    let data = [];
    let blocks = infiniteModel.infiniteCache.blocks;
    let blockKeys = Object.keys(blocks);
    for (let i = 0; i < blockKeys.length; i++) {
      let block = blocks[blockKeys[i]];
      if (block.state !== "loaded") {
        break;
      }

      for (let j = 0; j < block.rowNodes.length; j++) {
        if (!block.rowNodes[j].data) {
          // Terminating paging doesn't seem to be working the way it should be according to ag-grid API, skipping the rows fixes it but is duct tape.
          continue;
        }

        data.push(block.rowNodes[j].data);
      }
    }

    gridOptions.api.rowModel.setRowData(data);
    this.loadMoreIfNeeded();
  }

  private onGridReady(gridOptions: any) {
    let context = gridOptions.api.context;
    let infiniteModel = new agGrid.InfiniteRowModel();
    context.wireBean(infiniteModel);
    let oldWrapper = infiniteModel.gridOptionsWrapper;
    let newWrapper: any = new oldWrapper.__proto__.constructor();
    angular.extend(newWrapper, oldWrapper);
    newWrapper.isRowModelInfinite = () => true;
    infiniteModel.gridOptionsWrapper = newWrapper;
    infiniteModel.init();
    let oldReset = infiniteModel.reset.bind(infiniteModel);
    infiniteModel.reset = (() => {
      oldReset();
      this.dataSourceProxy.onInfiniteModelReset();

      this.table.gridOptions.api.showLoadingOverlay();
      infiniteModel.infiniteCache.addDestroyableEventListener(
        infiniteModel.infiniteCache.localEventService,
        "cacheUpdated",
        () => {
          this.table.gridOptions.api.hideOverlay();
          this.onInfiniteModelCacheUpdated(infiniteModel, gridOptions);
        }
      );
    }).bind(this);
    infiniteModel.reset();

    this.infiniteRowModel = infiniteModel;
  }
}
